import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { useInView } from "@/lib/useInView";
import { api, resolveImageUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { CATEGORIES } from "@/data/categories";
import { entryImage } from "@/lib/placeholders";
import { entryPath } from "@/lib/paths";
import { Skel } from "@/components/home/HomeStrips";

const CAT_ORDER = ["stores", "events", "creators"];   // patch 40: vending hidden for now

function categoryMeta(id, lang) {
  const c = CATEGORIES.find((x) => x.id === id);
  if (!c) return { short: id, title: id };
  return {
    short: c.short[lang] || c.short.en,
    title: c.title[lang] || c.title.en,
  };
}

function Dots({ count, index }) {
  if (count <= 1) return null;
  return (
    <div className="flex items-center gap-0.5" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className={`h-[3px] w-2 rounded-sm transition-colors duration-200 ${
            i === index ? "bg-amber-400" : "bg-stone-700/80"
          }`}
        />
      ))}
    </div>
  );
}

function PulseRow({ items, cat, kind, lang, t, flash }) {
  const meta = categoryMeta(cat, lang);
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1); // -1 or +1 — drives the slide
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const touchDX = useRef(0);
  const box = useRef(null);
  const onScreen = useInView(box);   // patch 30: autoplay only while the row is on screen

  const count = items.length;
  const safeIndex = count ? Math.min(index, count - 1) : 0;
  const entry = count ? items[safeIndex] : null;
  const value = entry ? (kind === "rising" ? entry.upvotes : entry.downvotes) : 0;

  // patch 28: a fresh vote on one of these entries brings it into view and makes the row glow
  const flashedIdx = flash ? items.findIndex((x) => flash.has(x.id)) : -1;
  useEffect(() => {
    if (flashedIdx >= 0) setIndex(flashedIdx);
  }, [flashedIdx]);
  const glowing = entry && flash && flash.has(entry.id);

  const go = (dir) => {
    if (count < 2) return;
    setDirection(dir > 0 ? 1 : -1);
    setIndex((i) => (i + dir + count) % count);
  };

  // Autoplay: advance every ~5s unless hovered / only one item
  useEffect(() => {
    if (count < 2 || paused || !onScreen) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      setDirection(1);
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(id);
  }, [count, paused, onScreen]);

  function onTouchStart(e) {
    touchX.current = e.touches[0].clientX;
    touchDX.current = 0;
    setPaused(true);
  }
  function onTouchMove(e) {
    if (touchX.current == null) return;
    touchDX.current = e.touches[0].clientX - touchX.current;
  }
  function onTouchEnd() {
    const dx = touchDX.current;
    touchX.current = null;
    touchDX.current = 0;
    // Resume autoplay shortly after touch ends
    setTimeout(() => setPaused(false), 400);
    if (Math.abs(dx) < 30) return;
    go(dx < 0 ? 1 : -1);
  }

  if (!entry) {
    return (
      <div
        data-testid={`pulse-${kind}-empty`}
        className="flex items-center gap-3 px-3 py-3 rounded-sm border border-dashed border-stone-700/60"
      >
        <div className="w-11 h-11 rounded-sm bg-stone-800/40 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.25em] text-stone-600 font-semibold">
            {meta.short}
          </div>
          <div className="font-display text-sm text-stone-600 mt-0.5 truncate">
            {kind === "rising" ? t.pulse.empty_rising : t.pulse.empty_falling}
          </div>
        </div>
      </div>
    );
  }

  const bgTone = kind === "rising" ? "bg-amber-500/5" : "bg-rose-500/5";
  const chevronBase =
    "shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-sm text-stone-500 hover:text-amber-300 transition-colors";
  const arrowCls =
    kind === "rising" ? "triangle-up triangle-big text-amber-400" : "triangle-down triangle-big text-rose-400";
  const voteCls = kind === "rising" ? "text-amber-300" : "text-rose-300";

  return (
    <div
      ref={box}
      data-testid={`pulse-${kind}-${cat}`}
      className={`relative flex items-center gap-2 pr-2 pl-1 py-2 rounded-sm border border-amber-500/10 hover:border-amber-500/35 ${bgTone} transition-colors duration-200 ${glowing ? "pulse-flash" : ""}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {count > 1 ? (
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Previous"
          data-testid={`pulse-${kind}-${cat}-prev`}
          className={chevronBase}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      ) : (
        <span className="w-5 shrink-0" aria-hidden />
      )}

      <div className="flex-1 min-w-0 relative overflow-hidden h-[52px]">
          <div
            key={entry.id}
            className="pulse-slide absolute inset-0 flex items-center gap-3"
            style={{ "--dx": `${direction * 24}px` }}
          >
            <Link
              to={entryPath(entry)}
              data-testid={`pulse-${kind}-${cat}-link`}
              className="flex-1 min-w-0 flex items-center gap-3 group"
            >
              <img
                src={resolveImageUrl(entryImage(entry), 96)}
                alt=""
                loading="lazy"
                className="w-11 h-11 rounded-sm object-cover shrink-0 border border-amber-500/15"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.25em] text-stone-500 font-semibold shrink-0">
                    {meta.short}
                  </div>
                  <Dots count={count} index={safeIndex} />
                </div>
                <div className="font-display text-sm sm:text-base font-bold text-stone-100 group-hover:text-amber-200 truncate flex items-center gap-1.5">
                  <span className="truncate">{entry.name}</span>
                  {kind === "rising" && entry.trending && (
                    <span
                      data-testid={`pulse-trending-${cat}`}
                      title={t.category.trending_hint}
                      className="shrink-0 inline-flex items-center justify-center text-amber-400 animate-pulse"
                      aria-label={t.category.trending}
                    >
                      <Flame className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
                {entry.location && (
                  <div className="text-[11px] text-stone-500 truncate">{entry.location}</div>
                )}
              </div>
            </Link>
          </div>
      </div>

      {glowing && <span className="pulse-plus" data-testid="pulse-plus">+1</span>}
      <div className={`flex items-center gap-2 pl-1 ${voteCls}`}>
        <span className={arrowCls} aria-hidden />
        <span
          key={`v-${entry.id}`}
          data-testid={`pulse-${kind}-${cat}-votes`}
          className="pulse-pop font-mono font-black text-xl sm:text-2xl leading-none tabular-nums"
        >
          {value}
        </span>
      </div>

      {count > 1 ? (
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Next"
          data-testid={`pulse-${kind}-${cat}-next`}
          className={chevronBase}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      ) : (
        <span className="w-5 shrink-0" aria-hidden />
      )}
    </div>
  );
}

function PulsePanel({ kind, data, t, lang, flash }) {
  const isRising = kind === "rising";
  return (
    <div
      data-testid={`pulse-panel-${kind}`}
      className={`panel rounded-sm p-5 lg:p-6 flex flex-col gap-4 ${
        isRising
          ? "border-amber-500/25 shadow-[0_0_32px_-20px_rgba(212,168,44,0.5)]"
          : "border-rose-500/25 shadow-[0_0_32px_-20px_rgba(244,63,94,0.4)]"
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-[10px] uppercase tracking-[0.3em] font-bold ${
              isRising ? "text-amber-400" : "text-rose-400"
            }`}
          >
            <span
              className={
                isRising
                  ? "inline-block triangle-up triangle-big mr-2 align-middle text-amber-400"
                  : "inline-block triangle-down triangle-big mr-2 align-middle text-rose-400"
              }
              aria-hidden
            />
            {isRising ? t.pulse.rising : t.pulse.falling}
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight mt-1 text-stone-100 leading-none">
            {isRising ? t.pulse.rising_sub : t.pulse.falling_sub}
          </h3>
        </div>
      </header>
      <div className="h-px bg-amber-500/10" />
      <div className="flex flex-col gap-2">
        {CAT_ORDER.map((cat) => (
          <PulseRow
            key={`${kind}-${cat}`}
            items={data?.[cat] || []}
            cat={cat}
            kind={kind}
            lang={lang}
            t={t}
            flash={flash}
          />
        ))}
      </div>
    </div>
  );
}

export default function CommunityPulse() {
  const { t, lang } = useI18n();
  const [data, setData] = useState({ top: {}, flop: {} });
  const [loading, setLoading] = useState(true);
  const [ticker, setTicker] = useState([]);
  const [flash, setFlash] = useState(null);
  const lastAt = useRef(null);

  // patch 28: every 30s (only while the page is visible) look for new activity → ticker + glow on the voted entries
  useEffect(() => {
    let alive = true;
    const poll = async (first) => {
      if (!first && document.visibilityState !== "visible") return;
      try {
        const { data: tk } = await api.get("/pulse/ticker");
        if (!alive) return;
        const items = tk.items || [];
        setTicker(items);
        const fresh = lastAt.current ? items.filter((i) => i.at > lastAt.current && i.kind === "vote") : [];
        if (items[0]) lastAt.current = items[0].at;
        if (fresh.length) {
          const { data: pd } = await api.get("/community-pulse");
          if (!alive) return;
          setData(pd || { top: {}, flop: {} });
          // up-votes glow in "Mais upvotes", down-votes in "Mais downvotes"
          setFlash({ up: new Set(fresh.filter((i) => i.dir !== "down").map((i) => i.entry_id)), down: new Set(fresh.filter((i) => i.dir === "down").map((i) => i.entry_id)) });
          setTimeout(() => alive && setFlash(null), 1700);
        }
      } catch {
        /* offline: try again next time */
      }
    };
    poll(true);
    const id = setInterval(() => poll(false), 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    api
      .get("/community-pulse")
      .then((r) => {
        if (!alive) return;
        setData(r.data || { top: {}, flop: {} });
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading)
    return (
      <section className="py-14 border-t border-amber-500/10 bg-[#080809]" data-testid="pulse-loading">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col items-center sm:items-start">
          <Skel className="h-3 w-40" />
          <Skel className="h-10 w-52 mt-3" />
          <div className="grid gap-5 lg:grid-cols-2 w-full mt-8">
            {[0, 1].map((k) => <Skel key={k} className="h-[300px] w-full" />)}
          </div>
        </div>
      </section>
    );
  const ago = (iso) => {
    const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    return m < 1 ? t.h27.ago_now : m < 60 ? t.h27.ago_min.replace("{n}", m) : t.h27.ago_h.replace("{n}", Math.round(m / 60));
  };
  const tickText = (i) =>
    (i.kind === "comment" ? t.h27.tick_comment : i.kind === "support" ? t.h29.tick_support : i.kind === "leader" ? t.h29.tick_leader
      : i.dir === "down" ? t.h27.tick_vote_down : t.h27.tick_vote_up).replace("{e}", i.name);

  return (
    <section
      data-testid="community-pulse"
      className="relative py-16 lg:py-24 border-t border-amber-500/10 bg-[#080809]"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent"
      />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        <div className="text-center sm:text-left mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-amber-400/90">
            {t.pulse.overline}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-2">
            <h2 className="relative font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-stone-100 leading-[0.95] flex items-center justify-center sm:justify-start gap-3 sm:gap-4">
              <svg className="pulse-ecg absolute left-1/2 sm:left-0 -translate-x-1/2 sm:translate-x-0 top-1/2 -translate-y-1/2 w-[280px] sm:w-[420px] h-16 -z-0 pointer-events-none" viewBox="0 0 300 60" fill="none" aria-hidden data-testid="pulse-ecg">
                <path d="M0 30 H95 L105 30 L112 12 L120 50 L128 4 L136 44 L142 30 H300" stroke="rgba(244,63,94,0.35)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
              <span className="relative">{t.pulse.title}</span>
              <img
                src="/pixo/heart.png"
                alt=""
                aria-hidden
                className="pulse-heart w-[30px] h-[26px] sm:w-[45px] sm:h-[39px] lg:w-[60px] lg:h-[52px]"
                data-testid="pulse-heart"
              />
            </h2>
            <p className="text-sm sm:text-base text-stone-400 max-w-md leading-relaxed">
              {t.pulse.lede}
            </p>
          </div>
          {!!ticker.length && (
            <div className="pulse-ticker mt-5 border-y border-amber-500/15 py-2 text-[13px]" data-testid="pulse-ticker" aria-live="off">
              <div className="pulse-ticker-track" style={{ "--dur": `${Math.max(20, ticker.length * 6)}s` }}>
                {[...ticker, ...ticker].map((i, k) => (
                  <Link key={k} to={i.slug && i.category ? `/${i.category}/${i.slug}` : `/e/${i.entry_id}`} className="inline-flex items-center gap-1.5 text-stone-300 hover:text-amber-200">
                    <span className={i.kind === "comment" ? "text-sky-300" : i.dir === "down" ? "text-rose-400" : "text-emerald-400"}>{i.kind === "comment" ? "💬" : i.dir === "down" ? "▼" : "▲"}</span>
                    {tickText(i)} <span className="text-stone-500">· {ago(i.at)}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
          <PulsePanel kind="rising" data={data.top} t={t} lang={lang} flash={flash?.up} />
          <PulsePanel kind="falling" data={data.flop} t={t} lang={lang} flash={flash?.down} />
        </div>
      </div>
    </section>
  );
}
